import { PureComponent, ReactNode } from 'react';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import Fuse from 'fuse.js';
import { RouteComponentProps } from 'react-router-dom';
import { Transaction as ApiTransaction } from '../api/fetchAllTransactions';
import LeftBar from '../components/common/NavBar';
import { RangeValue } from '../components/common/DatePicker';
import { renderBillingDashboard } from '../bridge/methods';
import { captureError } from './captureError';
import { dispatch } from '../bridge/events/dispatch';
import { DESTROY_CONTAINER } from '../bridge/events/types';

const fuseConfig = {
    keys: ['_name', '_phone', '_uhid'],
    threshold: 0.1,
    ignoreLocation: true,
    useExtendedSearch: true,
};

export type Transaction = ApiTransaction & { localId: string };

export type FilterData = { [k in PaymentStatusFilterType]: { count: number; amount?: number } };

type RouterProps = {
    paymentStatusFilter: PaymentStatusFilterType;
};

type Props = RouteComponentProps<RouterProps>;

export type PaymentStatusFilterType =
    | 'all-receipts'
    | 'all-transactions'
    | 'unbilled'
    | 'pending-payment'
    | 'paid';

export type TimeFilterType = 'today' | 'yesterday' | 'last-7-days';

export type PaymodeFilterObjType = { [k: string]: { amount: number } };
export type ServiceFilterObjType = { [k: string]: { amount: number; count: number } };

const startDateToday = moment().startOf('day').toISOString();
const endDateToday = moment().endOf('day').toISOString();

export const timeFiltersObj: {
    [k in TimeFilterType]: {
        id: TimeFilterType;
        label: string;
        startDate: string;
        endDate: string;
    };
} = {
    today: {
        id: 'today',
        label: 'Today',
        startDate: startDateToday,
        endDate: endDateToday,
    },
    yesterday: {
        id: 'yesterday',
        label: 'Yesterday',
        startDate: moment().subtract(1, 'days').startOf('day').toISOString(),
        endDate: moment().subtract(1, 'days').endOf('day').toISOString(),
    },
    'last-7-days': {
        id: 'last-7-days',
        label: 'Last 7 Days',
        startDate: moment().subtract(6, 'days').startOf('day').toISOString(),
        endDate: moment().endOf('day').toISOString(),
    },
};

type TransactionsWithMeta = {
    transactions: Transaction[];
    amount?: number;
};

export const paymentFiltersObj: {
    [k in PaymentStatusFilterType]: {
        id: PaymentStatusFilterType;
        label: string;
        getData: (array: Transaction[]) => TransactionsWithMeta;
    };
} = {
    'all-receipts': {
        id: 'all-receipts',
        label: 'All Receipts',
        getData: (array: Transaction[]) => ({ transactions: array }),
    },
    'all-transactions': {
        id: 'all-transactions',
        label: 'All Transactions',
        getData: (array: Transaction[]) => ({ transactions: array }),
    },
    unbilled: {
        id: 'unbilled',
        label: 'Unbilled Transactions',
        getData: (array: Transaction[]): TransactionsWithMeta => {
            const transactions = array.filter((t) => t.status === 'unbilled');

            return { transactions };
        },
    },
    'pending-payment': {
        id: 'pending-payment',
        label: 'Pending Payment',
        getData: (array: Transaction[]): TransactionsWithMeta => {
            const transactions = array.filter((t) => t.status && t.status === 'pending');

            let amount = 0;
            transactions.forEach((t) => {
                amount += t.amount || 0;
            });

            return { transactions, amount };
        },
    },
    paid: {
        id: 'paid',
        label: 'Paid',
        getData: (array: Transaction[]): TransactionsWithMeta => {
            const transactions = array.filter((t) => t.status && t.status === 'paid');

            let amount = 0;
            transactions.forEach((t) => {
                amount += t.amount || 0;
            });

            return { transactions, amount };
        },
    },
};

type SortOrder = 'asc' | 'desc';

export type FetchState = 'loading' | 'success' | 'error' | '';

type State = {
    fetchState: FetchState;
    filteredTransactions: Transaction[];
    sortOrder: SortOrder;
    searchFilter: string;
    initPaymentDialog?: Transaction;
    viewTransactionDetailsDialog?: Transaction;
    showServiceConfig: boolean;
    showPaymodeConfig: boolean;
    filteredServicesSet: Set<string>;
    filteredPaymodeSet: Set<string>;
    summaryDownloadTimeline?: string;
    summaryDownloadCustomDates?: string[];
    summaryReportIsLoading?: boolean;
    summaryDates?: RangeValue<moment.Moment>;
    paymentHistoryDownloadTimeline?: string;
    paymentHistoryDownloadCustomDates?: string[];
    paymentHistoryIsLoading?: boolean;
    paymentsHistoryDates?: RangeValue<moment.Moment>;
};

class PaymentsDashboard extends PureComponent<Props, State> {
    transactionsFuse = new Fuse([] as Transaction[], fuseConfig);
    counts: FilterData | undefined = undefined;
    allTransactions: Transaction[] = [];
    servicesSet: Set<string> = new Set();
    paymodeSet: Set<string> = new Set();
    servicesConfig: ServiceFilterObjType = {};
    paymodeConfig: PaymodeFilterObjType = {};

    initialState: State = {
        fetchState: '',
        filteredTransactions: [],
        searchFilter: '',
        sortOrder: 'asc',
        initPaymentDialog: undefined,
        viewTransactionDetailsDialog: undefined,
        showServiceConfig: false,
        showPaymodeConfig: false,
        filteredServicesSet: new Set(),
        filteredPaymodeSet: new Set(),
    };

    state: State = this.initialState;
    billingContainerId = 'billing';

    componentDidMount = (): void => {
        const configs = {
            containerId: this.billingContainerId,
            routeParams: this.props.match.params,
            queryParams: this.props.location.search,
        };

        if (!window.RENDER_CONFIGS) {
            window.RENDER_CONFIGS = [];
        }

        window.RENDER_CONFIGS.push(configs);

        try {
            renderBillingDashboard(configs);
        } catch (e) {
            console.log(e);
            captureError(e as Error);
        }
    };

    componentWillUnmount = (): void => {
        dispatch({ type: DESTROY_CONTAINER, containerId: this.billingContainerId });
        // window.removeEventListener(EKA_EVENT, this.onEkaEvents);
        window.RENDER_CONFIGS = window.RENDER_CONFIGS?.filter(
            (renderConfig) => renderConfig.containerId !== this.billingContainerId,
        );
    };

    render(): ReactNode {
        return (
            <>
                <div className="flex flex-col w-full h-full bg-white font-lato">
                    <Helmet>
                        <meta charSet="utf-8" />
                        <title>EKA - All Payments</title>
                    </Helmet>
                    <div className="grid h-full overflow-x-auto overflow-y-hidden grid-cols-queueGrid min-w-840 undefined">
                        <LeftBar />
                        <div
                            id={this.billingContainerId}
                            className="flex flex-col h-full col-span-11 overflow-hidden"
                        ></div>
                    </div>
                </div>
            </>
        );
    }
}

export default PaymentsDashboard;
